import {find, map, replace} from 'lodash';
import {Fragment, useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {sessionApi} from 'api';
import {CopyText, Divider, ExternalLink, Icon, InformationModal, ParticipantIcon, PredefinedNotifierType} from 'components';
import {useApiData} from 'hooks';
import {FullSessionAppointment} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {ParticipantRole} from 'types';
import {apiUtils, buildTranslationKey, getParticipantName, userAuthUtils} from 'utils';

type CopyIconProps = {
    text: string,
    className?: string,
    asLink?: boolean
};

const CopyIcon: React.FC<CopyIconProps> = ({text, className, asLink = false}: CopyIconProps) => (
    <div className={className}>
        {asLink ? <ExternalLink link={text} /> : text}
        <CopyText
            className='tw-ml-1 tw-align-top'
            text={text}
            copiedLabelOnly
            iconColor='default-dimmed'
            textInTooltip={false}
            labelPlacement='left'
            iconOnly
            iconSize='sm'
        />
    </div>
);

type Props = {
    open: boolean;
    data?: FullSessionAppointment;
    onClose: () => void;
};

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.APPOINTMENTS.TABLE;
const JOIN_INFO_T_KEYS = TRANSLATION_KEYS.IN_SESSION_PAGE.INVITE_MODAL;

export const ConnectInfoModal = ({open, data, onClose}: Props) => {
    const {t} = useTranslation();

    const isMobileAppAuthSource = useMemo(() => userAuthUtils.isMobileAppAuthSource(), []);

    const onSuccessDownload = useCallback((base64) => {
        apiUtils.saveFileFromBase64(`${t(JOIN_INFO_T_KEYS.INVITE_FILE_NAME)}.ics`, base64);
    }, [t]);

    const {send: downloadInviteFile} = useApiData(sessionApi.getInviteForCalendarFile,
        {onSuccess: onSuccessDownload, notificationType: PredefinedNotifierType.Save});

    const renderDownloadInviteFileLabel = useCallback((participantId: string) => (
        <div className='tw-my-4 tw-flex tw-items-end'>
            <span className='tw-mr-2'>{`${t(JOIN_INFO_T_KEYS.INVITE_FILE_NAME)}.ics`}</span>
            <Icon
                name='download'
                className='tw-inline'
                button
                margin={false}
                onClick={() => downloadInviteFile(data.session.id, participantId)}
            />
        </div>
    ), [data.session.id, downloadInviteFile, t]);

    const getLinkWithoutParams = (role: ParticipantRole, link: string, code: number, pin: number) => {
        if (role === ParticipantRole.Client || role === ParticipantRole.Guest) {
            return replace(link, `/${code}/${pin}`, '');
        }
        return '';
    };

    return (
        <InformationModal
            open={open}
            title={t(T_KEYS.COLUMNS.CONNECT)}
            fullWidth
            maxWidth='xs'
            onClose={onClose}
        >
            <div className=''>
                {map(data?.links, (link) => {
                    const participant = find(data?.session?.participants, (p) => p.id === link.id);
                    const tKey = buildTranslationKey(link.role);
                    const name = getParticipantName(participant?.data, T_KEYS.CONTENT[tKey] ? t(T_KEYS.CONTENT[tKey]) : link.role);
                    const {code, pin} = link;
                    const linkWithoutParams = getLinkWithoutParams(link.role, link.link, code, pin);

                    return (
                        <div key={link.id} className='tw-border-b tw-my-2 tw-border-default'>
                            <div className='tw-flex tw-items-center tw-mb-2'>
                                <ParticipantIcon role={link.role} />
                                <div className='tw-ts-base-bold tw-mx-2'>{name}</div>
                            </div>
                            <div>
                                <CopyIcon className='tw-text-break-word' text={link.link} asLink={!isMobileAppAuthSource} />
                                {(link.role === ParticipantRole.Client || link.role === ParticipantRole.Guest) &&
                                    <Fragment>
                                        <Divider className='tw-my-3' or />
                                        <CopyIcon
                                            className='tw-text-break-word'
                                            text={linkWithoutParams}
                                            asLink={!isMobileAppAuthSource}
                                        />
                                        <div className='tw-flex tw-mt-4'>
                                            <div className='tw-flex tw-items-baseline tw-mr-6'>
                                                <span className='tw-mr-2'>{t(JOIN_INFO_T_KEYS.CODE)}</span>
                                                <CopyIcon text={String(code)} />
                                            </div>
                                            <div className='tw-flex tw-items-baseline'>
                                                <span className='tw-mr-2'>{t(JOIN_INFO_T_KEYS.PIN)}</span>
                                                <CopyIcon text={String(pin)} />
                                            </div>
                                        </div>
                                    </Fragment>}
                                {!isMobileAppAuthSource && renderDownloadInviteFileLabel(link.id)}
                            </div>
                        </div>
                    );
                })}
            </div>
        </InformationModal>
    );
};
